import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel } from '@mui/material';
import { StyledCheckbox } from './CustomCheckbox.styles';
import { theme } from '../../constants/theme';

/**
 * CustomCheckbox Component
 * 
 * A checkbox component based on MUI Checkbox with consistent styling.
 * 
 * @component
 * @example
 * ```jsx
 * <CustomCheckbox
 *   checked={isChecked}
 *   onChange={(e) => setIsChecked(e.target.checked)}
 *   label="Accept terms and conditions"
 * />
 * ```
 */
const CustomCheckbox = ({
  checked = false,
  onChange,
  label,
  disabled = false,
  required = false,
  error = false,
  helperText,
  indeterminate = false,
  size = 'md',
  color = 'primary',
  className,
  ...props
}) => {
  const checkbox = (
    <Checkbox
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      required={required}
      indeterminate={indeterminate}
      size={size === 'sm' ? 'small' : 'medium'}
      sx={{
        color: error ? theme.colors.error.main : theme.colors.gray[400],
        '&.Mui-checked': {
          color: color === 'primary' ? theme.colors.primary.main : 
                 color === 'success' ? theme.colors.success.main :
                 color === 'error' ? theme.colors.error.main :
                 theme.colors.primary.main,
        },
        '&.MuiCheckbox-indeterminate': {
          color: color === 'primary' ? theme.colors.primary.main : 
                 color === 'success' ? theme.colors.success.main :
                 color === 'error' ? theme.colors.error.main :
                 theme.colors.primary.main,
        },
      }}
      {...props}
    />
  );

  return (
    <StyledCheckbox $error={error} $size={size} className={className}>
      {label ? (
        <FormControlLabel
          control={checkbox}
          label={label}
          disabled={disabled}
        />
      ) : (
        checkbox
      )}
      {helperText && (
        <div className="helper-text">{helperText}</div>
      )}
    </StyledCheckbox>
  );
};

CustomCheckbox.propTypes = {
  /** Checked state */
  checked: PropTypes.bool,
  /** Callback fired when the state changes */
  onChange: PropTypes.func.isRequired,
  /** Label text */
  label: PropTypes.string,
  /** Disable the checkbox */
  disabled: PropTypes.bool,
  /** Mark as required */
  required: PropTypes.bool,
  /** Show error state */
  error: PropTypes.bool,
  /** Helper text below the checkbox */
  helperText: PropTypes.string,
  /** Show indeterminate state */
  indeterminate: PropTypes.bool,
  /** Size variant */
  size: PropTypes.oneOf(['sm', 'md']),
  /** Color variant */
  color: PropTypes.oneOf(['primary', 'success', 'error']),
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default CustomCheckbox;
